import {
  ArrowLeftStartOnRectangleIcon,
  BanknotesIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  EnvelopeIcon,
  CalculatorIcon,
  GiftIcon,
  PhoneArrowUpRightIcon,
  RocketLaunchIcon,
  ShareIcon,
  TrophyIcon,
  UserIcon,
  PresentationChartLineIcon,
  InboxStackIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  HeartIcon,
  IdentificationIcon,
  FunnelIcon,
  ArrowTrendingUpIcon,
} from "@heroicons/react/24/outline";
import {
  ClipboardDocumentCheckIcon,
  HomeIcon,
  PhoneIcon,
  QueueListIcon,
} from "@heroicons/react/24/solid";

import {
  AnnouncementSpeakerIcon,
  ChatBubbleEllipsisIcon,
  CoachIcon,
  CogsIcon,
  LeaderboardIcon,
  MoneyFunnelIcon,
  UserMicrophoneIcon,
} from "shared/ui/icons";

import { NOTIFICATION_MAPPED_IDS } from "@/components/navigation/shared/notification-label";
import { PrimaryNavigationItemCollapsableWithIconI } from "@/components/navigation/primary/interfaces";
import Router from "@/helpers/router";
import { ValueOfObjectFields } from "shared/lib/interfaces/utils";
import { MANAGE_FEATURES, MANAGE_ROLES } from "@/api/routes/manage";

//TODO update pipeline page urls
export const MY_CAMPAIGNS_ITEM_ID = "my_campaigns";
export const PIPELINE_NAVIGATION_ITEM_ID = "accounts";
export const REFERRALS_ITEM_ID = "referrals";
export const SETTINGS_ITEM_ID = "settings";
export const SALES_FLOOR_ITEM_ID = "sales_floor";
export const TEAM_ITEM_ID = "team";

export const NAV_GROUP_NAMES = {
  HOME: "home",
  MY_WEEK: "my_week",
  LEADS: "leads",
  CAMPAIGNS: "campaigns",
  PERFORMANCE: "performance",
  MESSAGES: "messages",
  SETTINGS: "settings",
  ONBOARDING: "onboarding",
  TEAM: "team",
} as const;

type NavGroupNamesI = ValueOfObjectFields<typeof NAV_GROUP_NAMES>;

// All nav items organized by their group structures.
// Remember to reference the item you want from here into the appropriate menu array (found further below).
export const NAV_V3_ITEMS: Record<
  NavGroupNamesI,
  PrimaryNavigationItemCollapsableWithIconI
> = {
  [NAV_GROUP_NAMES.HOME]: {
    text: "Home",
    icon: <HomeIcon />,
    href: Router.dashboard(),
    disabled: false,
  },
  // Shown only while the backend's dial block switch is on (navbar).
  [NAV_GROUP_NAMES.MY_WEEK]: {
    text: "My week",
    icon: <CalendarDaysIcon />,
    href: Router.myWeek(),
    disabled: false,
  },
  [NAV_GROUP_NAMES.LEADS]: {
    text: "Leads",
    id: PIPELINE_NAVIGATION_ITEM_ID,
    isCollapsed: true,
    icon: <CurrencyDollarIcon />,
    children: [
      {
        text: "Working lists",
        href: Router.lists(),
        icon: <QueueListIcon />,
      },
      {
        text: "Open pipeline",
        href: Router.accounts(),
        icon: <MoneyFunnelIcon />,
      },
      {
        text: "History",
        href: Router.callHistory(),
        icon: <PhoneIcon />,
      },
    ],
  },
  [NAV_GROUP_NAMES.CAMPAIGNS]: {
    text: "Campaigns",
    isCollapsed: false,
    icon: <AnnouncementSpeakerIcon />,
    children: [
      {
        text: "My campaigns",
        id: MY_CAMPAIGNS_ITEM_ID,
        href: Router.myCampaigns(),
        icon: <UserMicrophoneIcon />,
      },
      {
        text: "Discover campaigns",
        href: Router.marketplace(),
        icon: <RocketLaunchIcon />,
      },
    ],
  },
  [NAV_GROUP_NAMES.PERFORMANCE]: {
    id: NOTIFICATION_MAPPED_IDS.PERFORMANCE,
    text: "Performance",
    icon: <LeaderboardIcon />,
    tooltip:
      "Check out the best-performing callers. Could your name be among the distinguished few?",
    isCollapsed: true,
    disabled: false,
    children: [
      {
        text: "Dashboard",
        tooltip: "View your achievements. Take a moment to let it all in.",
        href: Router.performanceDashboard(),
        icon: <PresentationChartLineIcon />,
      },
      {
        text: "Leaderboards",
        tooltip: "View your achievements. Take a moment to let it all in.",
        href: Router.leaderboard(),
        icon: <ChartBarIcon />,
      },
      {
        text: "Trophies",
        tooltip: "View your achievements. Take a moment to let it all in.",
        href: Router.trophies(),
        icon: <TrophyIcon />,
      },
      {
        text: "Coaching",
        id: NOTIFICATION_MAPPED_IDS.COACHING,
        href: Router.coaching(),
        icon: <CoachIcon />,
      },
    ],
  },
  [NAV_GROUP_NAMES.MESSAGES]: {
    id: NOTIFICATION_MAPPED_IDS.TOTAL_MESSAGES,
    text: "Messages",
    icon: <ChatBubbleEllipsisIcon />,
    isCollapsed: true,
    disabled: false,
    children: [
      {
        text: "Customer messages",
        id: NOTIFICATION_MAPPED_IDS.MESSAGES,
        href: Router.messages(),
        icon: <EnvelopeIcon />,
      },
      {
        text: "Email Inbox",
        id: NOTIFICATION_MAPPED_IDS.EMAILS,
        href: Router.emailInbox(),
        icon: <InboxStackIcon />,
      },
    ],
  },
  [NAV_GROUP_NAMES.SETTINGS]: {
    text: "Settings",
    id: SETTINGS_ITEM_ID,
    icon: <CogsIcon className="w-5" />,
    isCollapsed: true,
    children: [
      {
        text: "My Profile",
        href: Router.settingsProfile(),
        icon: <UserIcon />,
      },
      {
        text: "Call Forwarding",
        href: Router.settingsCallForwarding(),
        icon: <PhoneArrowUpRightIcon />,
      },
      {
        text: "Payouts",
        href: Router.settingsPayouts(),
        icon: <BanknotesIcon />,
      },
      {
        text: "Referrals",
        id: REFERRALS_ITEM_ID,
        icon: <ShareIcon />,
        href: Router.settingsReferrals(),
      },
      {
        text: "Sign out",
        href: Router.logout(),
        icon: <ArrowLeftStartOnRectangleIcon />,
      },
    ],
  },
  // --- C-14 roles: the Team group (F1 owns this block; F3 appends its pages
  // inside `children`). Shown only to role holders while the backend's
  // `user_roles_enabled` switch is on (navbar/use-team-nav-items).
  [NAV_GROUP_NAMES.TEAM]: {
    text: "Team",
    id: TEAM_ITEM_ID,
    icon: <UserGroupIcon />,
    isCollapsed: true,
    children: [
      // F3: the block pages. A Pod Lead's are read-only.
      {
        text: "Calendar",
        href: Router.manageCalendar(),
        icon: <CalendarDaysIcon />,
        roles: [MANAGE_ROLES.ADMIN, MANAGE_ROLES.LGA, MANAGE_ROLES.POD_LEAD],
      },
      // C-12 wave 2: Rep, Campaign and Data Health as tabs; a Pod Lead gets
      // the first two, read-only.
      {
        text: "Health",
        href: Router.manageHealth(),
        icon: <HeartIcon />,
        roles: [MANAGE_ROLES.ADMIN, MANAGE_ROLES.LGA, MANAGE_ROLES.POD_LEAD],
      },
      // C-16 S3: Command Center's Pipeline (Recovery Queue, Upcoming, …).
      // An LGA's own workspaces, an Admin's all; no Pod Lead (C16-D305).
      // Shown while user_roles_pipeline_enabled is on (manage/me features).
      {
        text: "Pipeline",
        href: Router.managePipeline(),
        icon: <FunnelIcon />,
        roles: [MANAGE_ROLES.ADMIN, MANAGE_ROLES.LGA],
        feature: MANAGE_FEATURES.PIPELINE,
      },
      // C-16 S3: where each campaign group's month will land, against its
      // target, and who is at risk. Dark until user_roles_forecast_enabled.
      {
        text: "Forecast",
        href: Router.manageForecast(),
        icon: <ArrowTrendingUpIcon />,
        roles: [MANAGE_ROLES.ADMIN, MANAGE_ROLES.LGA],
        feature: MANAGE_FEATURES.FORECAST,
      },
      // W2 (C-14 phase 2): C-11's workspace analytics. Not a Pod Lead's:
      // Command Center's Pipeline is LGA-only (C14-W2-D80). Shown while
      // user_roles_analytics_enabled is on (manage/me features).
      {
        text: "Analytics",
        href: Router.manageAnalytics(),
        icon: <PresentationChartLineIcon />,
        roles: [MANAGE_ROLES.ADMIN, MANAGE_ROLES.LGA],
        feature: MANAGE_FEATURES.ANALYTICS,
      },
      // C-14 phase 2 W3: the workspace's callers (no Pod Lead view).
      {
        text: "Reps",
        href: Router.manageReps(),
        icon: <IdentificationIcon />,
        roles: [MANAGE_ROLES.ADMIN, MANAGE_ROLES.LGA],
        feature: MANAGE_FEATURES.REPS,
      },
      // C-16 S1: contests for the workspace's callers; Pod Leads get Rewards
      // only (C16-D305).
      {
        text: "Incentives",
        href: Router.manageIncentives(),
        icon: <GiftIcon />,
        roles: [MANAGE_ROLES.ADMIN, MANAGE_ROLES.LGA],
        feature: MANAGE_FEATURES.INCENTIVES,
      },
      // C-16 S2: Command Center's EOM reconciliation. An LGA's own
      // workspaces, an Admin's all plus the money actions; no Pod Lead.
      // Shown while user_roles_eom_enabled is on (manage/me features).
      {
        text: "EOM",
        href: Router.manageEom(),
        icon: <CalculatorIcon />,
        roles: [MANAGE_ROLES.ADMIN, MANAGE_ROLES.LGA],
        feature: MANAGE_FEATURES.EOM,
      },
      {
        text: "Admin",
        href: Router.manageAdmin(),
        icon: <ShieldCheckIcon />,
        roles: [MANAGE_ROLES.ADMIN],
      },
      {
        // An LGA manages their workspaces' pods on the Admin page, where
        // Pods is their only tab. No `?tab=`: the nav's active state matches
        // on the route alone.
        text: "Pods",
        href: Router.manageAdmin(),
        icon: <UserGroupIcon />,
        roles: [MANAGE_ROLES.LGA],
      },
      // C-12 C: an Admin's shortcut to the Admin page's Pods tab (an Admin
      // sees only items listing "admin", so never both Pods items).
      {
        text: "Pods",
        href: Router.manageAdmin("pods"),
        icon: <UserGroupIcon />,
        roles: [MANAGE_ROLES.ADMIN],
      },
      // C-14 phase 2 W1's Campaigns page moved into Calendar → Capacity
      // (C-12 C, C12-D250); /manage/campaigns redirects there.
      // C-14 phase 2 W4: Team → Leaderboard (Admin, LGA, Pod Lead).
      {
        text: "Leaderboard",
        href: Router.manageLeaderboard(),
        icon: <TrophyIcon />,
        roles: [MANAGE_ROLES.ADMIN, MANAGE_ROLES.LGA, MANAGE_ROLES.POD_LEAD],
        feature: MANAGE_FEATURES.INSIGHTS,
      },
      // C-14 phase 2 W4's Team → Activity is a tab on the Admin page since
      // C-16 S5 (C16-D450); /manage/activity redirects there.
    ],
  },
  // --- end C-14 block ---
  [NAV_GROUP_NAMES.ONBOARDING]: {
    text: "Settings",
    id: SETTINGS_ITEM_ID,
    icon: <CogsIcon className="w-5 text-white" />,
    isCollapsed: true,
    children: [
      {
        text: "Sign out",
        href: Router.logout(),
        icon: <ArrowLeftStartOnRectangleIcon />,
      },
    ],
  },
};

// C-16 S6: Leads → To-Dos, between Open pipeline and History, while
// `daily_todos_enabled` is on (see useLeadsNavItem).
export const TODOS_NAV_ITEM: PrimaryNavigationItemCollapsableWithIconI = {
  text: "To-Dos",
  tooltip: "Your daily follow-ups: meetings to confirm, no-shows to rebook.",
  href: Router.todos(),
  icon: <ClipboardDocumentCheckIcon />,
};

// C-16 S1: Performance → Rewards, after Leaderboards, while `rewards_enabled`
// is on (see usePrimaryNavItems).
export const REWARDS_NAV_ITEM: PrimaryNavigationItemCollapsableWithIconI = {
  text: "Rewards",
  tooltip: "Your contests: what's running, your standing and what you've won.",
  href: Router.rewards(),
  icon: <GiftIcon />,
};

export const TOP_CALLS_NAV_ITEM: PrimaryNavigationItemCollapsableWithIconI = {
  text: "Top Calls",
  href: Router.topCalls(),
  icon: <PhoneIcon />,
};

// These nav items persist across all screen sizes

// These nav items hide on collapsed screen sizes
export const NAV_V3_ITEMS_PRIMARY: Array<PrimaryNavigationItemCollapsableWithIconI> =
  [
    NAV_V3_ITEMS[NAV_GROUP_NAMES.CAMPAIGNS],
    NAV_V3_ITEMS[NAV_GROUP_NAMES.PERFORMANCE],
  ];
